import {Edge, Node} from 'reactflow';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode,
  EntityMap_entityMap_EntityMapNode_edges_EntityMapEdgeNode as EntityMapEdgeNode,
  EntityMap_entityMap_EntityMapNode_connectedSchemas_EntityMapConnectedSchemaNode as EntityMapConnectedSchemaNode,
} from './__generated/EntityMap';
import {ENTITY_MAP_EDGES_PER_NODE_LIMIT, ENTITY_MAP_TOTAL_NODE_LIMIT} from './EntityMapConstants';

export function computeNodeCountBySchema(
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of graphNodesById.values()) {
    if (!isInaccessible(node.data) && node.data.schemaName != null) {
      counts.set(node.data.schemaName, (counts.get(node.data.schemaName) ?? 0) + 1);
    }
  }
  return counts;
}

export function mergePeakCounts(
  existing: Map<string, number>,
  current: Map<string, number>
): Map<string, number> {
  const result = new Map(existing);
  for (const [schema, count] of current) {
    result.set(schema, Math.max(result.get(schema) ?? 0, count));
  }
  return result;
}

/**
 * Graph nodes keyed by entity id, edges keyed by edge id, a list of fully expanded entity ids, and a list of
 * entity ids that were not expanded due to the product limit.
 */
export interface EntityMapGraph {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
  connectedSchemas: Array<EntityMapConnectedSchemaNode>;
}

/**
 * Parses entity map entities and edges into react flow nodes and edges to return an EntityMapGraph.
 */
export function parseEntityMapIntoEntityMapGraph({
  entities,
  edges,
  connectedSchemas,
}: {
  entities: Array<EntityMapEntityNodeOrInaccessibleInfoNode>;
  edges: Array<EntityMapEdgeNode>;
  connectedSchemas: Array<EntityMapConnectedSchemaNode>;
}): EntityMapGraph {
  const graphNodesById = new Map(
    entities.map((entity) => entityMapEntityNodeToReactFlowNode(entity)).map((node) => [node.id, node])
  );
  const graphEdgesById = new Map(edges.map((edge) => [edge.id, entityMapEdgeNodeToReactFlowEdge(edge)]));
  return {
    graphNodesById,
    graphEdgesById,
    connectedSchemas,
  };
}

/**
 * Converts an EntityMapEntityNode to a ReactFlow Node
 *
 * @param entity The EntityMapEntityNode to convert
 * @returns The ReactFlow Node
 */
export function entityMapEntityNodeToReactFlowNode(
  entity: EntityMapEntityNodeOrInaccessibleInfoNode
): Node<EntityMapEntityNodeOrInaccessibleInfoNode> {
  return {
    id: isInaccessible(entity) ? entity.invalidId : entity.id,
    type: 'entity',
    data: entity,
    position: {x: 0, y: 0},
  };
}

/**
 * Converts an EntityMapEdgeNode to a ReactFlow Edge
 *
 * @param edge The EntityMapEdgeNode to convert
 * @returns The ReactFlow Edge
 */
export function entityMapEdgeNodeToReactFlowEdge(edge: EntityMapEdgeNode): Edge {
  return {
    id: edge.id,
    source: edge.sourceId,
    target: edge.targetId,
    type: 'entityEdge',
    // TODO(BNCH-126927): Provide a label for the edge
    ...getTestProps({component: 'EntityMapEdge', key: edge.id}),
  };
}

/**
 * Merges two graph nodes by id, preserving the positions of existing nodes.
 */
export function mergeGraphNodesById(
  oldGraphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>,
  newGraphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>
): Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>> {
  const graphNodesById = new Map(oldGraphNodesById.entries());

  newGraphNodesById.forEach((graphNode, entityId) => {
    if (graphNodesById.has(entityId)) {
      graphNodesById.set(entityId, {
        ...graphNodesById.get(entityId)!,
        data: graphNode.data,
      });
    } else {
      graphNodesById.set(entityId, graphNode);
    }
  });

  return graphNodesById;
}

function extractEdgeCountsFromReactFlowNodes(
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>
): Map<string, number> {
  return new Map(
    Array.from(graphNodesById.entries()).map(([entityId, graphNode]) => [
      entityId,
      isInaccessible(graphNode.data) ? 0 : graphNode.data.edgeCount,
    ])
  );
}

/**
 * Counts how many edges each node participates in within the current graph.
 */
function extractCurrentGraphEdgeCountsById(graphEdgesById: Map<string, Edge>): Map<string, number> {
  const edgeCountsById = new Map<string, number>();

  for (const edge of graphEdgesById.values()) {
    const sourceCount = edgeCountsById.get(edge.source) || 0;
    const targetCount = edgeCountsById.get(edge.target) || 0;

    edgeCountsById.set(edge.source, sourceCount + 1);
    edgeCountsById.set(edge.target, targetCount + 1);
  }

  return edgeCountsById;
}

/**
 * Extracts fully expanded entity ids from a graph nodes map by comparing the node's total edge count against
 * how many edges it participates in within the current graph.
 */
export function extractFullyExpandedEntityIds({
  graphNodesById,
  graphEdgesById,
}: Pick<EntityMapGraph, 'graphNodesById' | 'graphEdgesById'>): Set<string> {
  const graphNodesCountsById = extractEdgeCountsFromReactFlowNodes(graphNodesById);
  const currentGraphEdgeCountsById = extractCurrentGraphEdgeCountsById(graphEdgesById);

  const fullyExpandedEntityIds = new Set(
    Array.from(graphNodesById.entries())
      .filter(([entityId, graphNode]) => {
        if (isInaccessible(graphNode.data)) {
          return false;
        }
        const totalEdgeCount: number = graphNodesCountsById.get(entityId)!;
        const currentEdgeCount: number = currentGraphEdgeCountsById.get(entityId) || 0;
        // Fuzzy matching is used here to handle scenarios where after loading edges on the frontend, an edge
        // is removed on the backend. In that case, the loaded edge count will always be greater than the
        // current edge count, as extra edges on the frontend will not be removed.
        return totalEdgeCount <= currentEdgeCount;
      })
      .map(([entityId, _]) => entityId)
  );

  return fullyExpandedEntityIds;
}

/**
 * Filters edges to only those connecting visible (non-hidden-schema) nodes.
 */
export function getSchemaFilteredEdges(
  graphEdgesById: Map<string, Edge>,
  visibleNodeIds: Set<string> | null
): Array<Edge> {
  const edges = Array.from(graphEdgesById.values());
  if (!visibleNodeIds) {
    return edges;
  }
  return edges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));
}

/**
 * Caps edges per node to ENTITY_MAP_EDGES_PER_NODE_LIMIT, prioritizing edges that
 * improve schema diversity around over-limit hubs.
 */
export function getEdgesCappedByProductLimits(
  visibleEdges: Array<Edge>,
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>
): {cappedEdges: Array<Edge>; edgesCapped: boolean} {
  const edgeCountPerNode = new Map<string, number>();
  for (const edge of visibleEdges) {
    edgeCountPerNode.set(edge.source, (edgeCountPerNode.get(edge.source) ?? 0) + 1);
    edgeCountPerNode.set(edge.target, (edgeCountPerNode.get(edge.target) ?? 0) + 1);
  }

  const hasOverLimitNode = Array.from(edgeCountPerNode.values()).some(
    (count) => count > ENTITY_MAP_EDGES_PER_NODE_LIMIT
  );
  if (!hasOverLimitNode) {
    return {cappedEdges: visibleEdges, edgesCapped: false};
  }

  const schemaNameByNodeId = new Map<string, string | null>();
  for (const node of graphNodesById.values()) {
    schemaNameByNodeId.set(node.id, isInaccessible(node.data) ? null : node.data.schemaName);
  }

  const edgeCountBySchema = new Map<string, number>();
  for (const edge of visibleEdges) {
    const sourceSchema = schemaNameByNodeId.get(edge.source);
    const targetSchema = schemaNameByNodeId.get(edge.target);
    if (sourceSchema != null) {
      edgeCountBySchema.set(sourceSchema, (edgeCountBySchema.get(sourceSchema) ?? 0) + 1);
    }
    if (targetSchema != null) {
      edgeCountBySchema.set(targetSchema, (edgeCountBySchema.get(targetSchema) ?? 0) + 1);
    }
  }

  const overLimitNodes = new Set(
    Array.from(edgeCountPerNode.entries())
      .filter(([, count]) => count > ENTITY_MAP_EDGES_PER_NODE_LIMIT)
      .map(([id]) => id)
  );

  // Prefer edges that improve schema diversity around over-limit hubs:
  // edges connecting to rarer schemas sort first so they survive the per-node budget.
  function edgePriority(edge: Edge): number {
    const other = overLimitNodes.has(edge.source) ? edge.target : edge.source;
    const schema = schemaNameByNodeId.get(other);
    return schema != null ? (edgeCountBySchema.get(schema) ?? 0) : 0;
  }

  const sortedEdges = [...visibleEdges].sort((a, b) => edgePriority(a) - edgePriority(b));

  const acceptedEdgeCountByNodeId = new Map<string, number>();
  const result: Array<Edge> = [];
  for (const edge of sortedEdges) {
    const sourceBudget = acceptedEdgeCountByNodeId.get(edge.source) ?? 0;
    const targetBudget = acceptedEdgeCountByNodeId.get(edge.target) ?? 0;
    if (sourceBudget >= ENTITY_MAP_EDGES_PER_NODE_LIMIT || targetBudget >= ENTITY_MAP_EDGES_PER_NODE_LIMIT) {
      continue;
    }
    acceptedEdgeCountByNodeId.set(edge.source, sourceBudget + 1);
    acceptedEdgeCountByNodeId.set(edge.target, targetBudget + 1);
    result.push(edge);
  }
  return {cappedEdges: result, edgesCapped: true};
}

/**
 * Caps nodes to ENTITY_MAP_TOTAL_NODE_LIMIT, keeping the root node and prioritizing
 * nodes by visible degree (most connected first).
 */
export function getNodesCappedByProductLimits(
  graphNodes: Array<Node<EntityMapEntityNodeOrInaccessibleInfoNode>>,
  cappedEdges: Array<Edge>,
  edgesCapped: boolean,
  entityId: string
): Array<Node<EntityMapEntityNodeOrInaccessibleInfoNode>> {
  let nodes = graphNodes;
  if (edgesCapped) {
    const nodeIdsReferencedByDisplayEdges = new Set<string>();
    nodeIdsReferencedByDisplayEdges.add(entityId);
    for (const edge of cappedEdges) {
      nodeIdsReferencedByDisplayEdges.add(edge.source);
      nodeIdsReferencedByDisplayEdges.add(edge.target);
    }
    nodes = nodes.filter((node) => nodeIdsReferencedByDisplayEdges.has(node.id));
  }
  if (nodes.length <= ENTITY_MAP_TOTAL_NODE_LIMIT) {
    return nodes;
  }
  const edgeCountPerNode = new Map<string, number>();
  for (const edge of cappedEdges) {
    edgeCountPerNode.set(edge.source, (edgeCountPerNode.get(edge.source) ?? 0) + 1);
    edgeCountPerNode.set(edge.target, (edgeCountPerNode.get(edge.target) ?? 0) + 1);
  }
  const nodesSortedByVisibleDegreeDesc = [...nodes].sort(
    (a, b) => (edgeCountPerNode.get(b.id) ?? 0) - (edgeCountPerNode.get(a.id) ?? 0)
  );
  const kept = new Set<string>([entityId]);
  for (const node of nodesSortedByVisibleDegreeDesc) {
    if (kept.size >= ENTITY_MAP_TOTAL_NODE_LIMIT) {
      break;
    }
    kept.add(node.id);
  }
  return nodes.filter((node) => kept.has(node.id));
}

/**
 * Computes per-schema visible/total node counts for the filter sidebar display.
 */
export function computeDisplayNodeCountsBySchema(
  cappedGraphNodes: Array<Node<EntityMapEntityNodeOrInaccessibleInfoNode>>,
  peakNodeCountBySchema: Map<string, number>,
  allSchemaNames: Iterable<string>
): Map<string, {visible: number; total: number}> {
  const visibleNodeCountBySchema = new Map<string, number>();
  for (const node of cappedGraphNodes) {
    if (!isInaccessible(node.data) && node.data.schemaName != null) {
      visibleNodeCountBySchema.set(
        node.data.schemaName,
        (visibleNodeCountBySchema.get(node.data.schemaName) ?? 0) + 1
      );
    }
  }

  const result = new Map<string, {visible: number; total: number}>();
  for (const [schema, total] of peakNodeCountBySchema) {
    result.set(schema, {visible: visibleNodeCountBySchema.get(schema) ?? 0, total});
  }
  for (const schema of allSchemaNames) {
    if (!result.has(schema)) {
      result.set(schema, {visible: 0, total: 0});
    }
  }
  return result;
}
